import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  coverService,
  IOdbCoverDetail,
  IOdbCoverDetailDivisionSummaryCategories,
  OdbCoverDetailDivision,
  OdbCoverDetailDivisionType,
} from '@dch/billing-business-logic';
import { TranslationService } from '@dch/ng-common';
import { tap } from 'rxjs/operators';
import { ApplicationState } from 'src/app/state/reducers';
import { selectIsUserSE } from 'src/app/state/selectors/customer-account.selector';
import { OnDestroyHookDirective } from '../common/hooks';
import { ICategoryClickEvent } from './cover-details-division.component';

export interface IDetailsClickEvent
  extends Omit<ICategoryClickEvent, 'category'> {
  category?: keyof IOdbCoverDetailDivisionSummaryCategories;
  address?: string;
}

@Component({
  selector: 'my-bill-cover-details',
  templateUrl: './cover-details.component.html',
  standalone: false,
})
export class CoverDetailsComponent
  extends OnDestroyHookDirective
  implements OnInit
{
  @Input() coverDetail: IOdbCoverDetail;
  @Input() testIDPrefix: string;
  @Output() detailsClick = new EventEmitter<IDetailsClickEvent>();

  displayedCoverDetail: IOdbCoverDetail;
  displaysSimplifiedLayout: boolean;
  hasZeroAmountInPackMobileDivisions: boolean;
  zeroUsageDivisions: Array<OdbCoverDetailDivision>;
  isVatInclusiveTextVisible = false;

  readonly selectIsUserSE$ = this.store.select(selectIsUserSE);

  constructor(
    private translationService: TranslationService,
    private store: Store<ApplicationState>
  ) {
    super();
  }

  ngOnInit() {
    this.displaysSimplifiedLayout =
      this.coverDetail.divisions.length === 1 &&
      this.coverDetail.divisions[0].summaryCategories === undefined;

    const filteredCoverDetail =
      coverService.hideZeroAmountInPackMobileDivisions(this.coverDetail);

    if (
      filteredCoverDetail.divisions.length !== this.coverDetail.divisions.length
    ) {
      this.hasZeroAmountInPackMobileDivisions = true;
      this.displayedCoverDetail = filteredCoverDetail;
      this.zeroUsageDivisions = this.getZeroUsageDivisions(
        this.coverDetail.divisions,
        filteredCoverDetail.divisions
      );
    } else {
      this.displayedCoverDetail = this.coverDetail;
    }

    if (this.displaysSimplifiedLayout) {
      this.addSubscription(
        this.selectIsUserSE$
          .pipe(
            tap((value: boolean) => {
              this.isVatInclusiveTextVisible =
                value &&
                (this.displayedCoverDetail.divisions[0].type === 'settlement' ||
                  this.displayedCoverDetail.divisions[0].type === 'thirdParty');
            })
          )
          .subscribe()
      );
    }
  }

  onCategoryClick({
    division,
    category,
    amount,
  }: {
    division: OdbCoverDetailDivision;
    category?: keyof IOdbCoverDetailDivisionSummaryCategories;
    amount: string;
  }) {
    this.detailsClick.emit({
      division,
      category,
      amount,
      address: this.getAddressDivisionIdentifier(this.coverDetail.divisions),
    });
  }

  getAddressDivisionIdentifier(
    divisions: Array<OdbCoverDetailDivision>
  ): string | undefined {
    const addressDivision = divisions.find(
      (division) => division.type === 'address'
    );
    if (addressDivision !== undefined) {
      return coverService.getDivisionIdentifierLabel(
        addressDivision.type,
        (key) => this.translationService.getTranslatedValueFor(key),
        addressDivision.identifier
      );
    }
  }

  getItemTitle(
    identifier: string | undefined,
    category: keyof IOdbCoverDetailDivisionSummaryCategories | undefined,
    divisionType: OdbCoverDetailDivisionType
  ): string {
    return coverService.getCoverDetailItemTitle(
      identifier,
      category,
      divisionType,
      (key: string) => this.translationService.getTranslatedValueFor(key)
    );
  }

  getZeroUsageDivisions(
    allDivisions: Array<OdbCoverDetailDivision>,
    filteredDivisions: Array<OdbCoverDetailDivision>
  ) {
    return allDivisions.filter(
      (item) =>
        !filteredDivisions.some(
          (itemToBeRemoved) =>
            item.type === 'address' ||
            itemToBeRemoved.identifier === item.identifier
        )
    );
  }
}
